import { useCallback } from 'react'
import { useRouter } from 'next/router'

import { AppLanguage } from '~/types/locale'
import { isSubscriptionRequiredForUser, userHasSubscription } from '~/redux/utils/subscription'
import { IUser } from '~/types/user-info-types'

interface SubscriptionAndBookingStatusReturnType {
  isSubscriptionRequired: boolean
  hasBookings: boolean
}

interface ReturnType {
  runCallbackWithSubscriptionAndBookingStatus: (
    user: IUser,
    onLoginCallback: (isSubscriptionRequired: boolean, hasBookings: boolean) => void,
  ) => void
  getSubscriptionAndBookingStatus: (user: IUser) => SubscriptionAndBookingStatusReturnType
  checkIfSubscriptionRequired: (user: IUser | null) => boolean
  checkIfUserHasActiveTodPackage: (user: IUser) => boolean
}

export default function useCheckSubscriptionAndBookingStatus(): ReturnType {
  const { locale } = useRouter()

  const checkIfSubscriptionRequired = useCallback(
    (user: IUser | null): boolean => {
      const UALocale = locale === AppLanguage.UA

      if (!user) {
        return true
      }

      return !UALocale && isSubscriptionRequiredForUser(user) && !userHasSubscription(user)
    },
    [locale],
  )

  const checkIfUserHasBookings = useCallback((user: IUser) => {
    const hasBookings = Boolean(user?.studentInfo?.bookings?.length)
    return hasBookings
  }, [])

  const checkIfUserHasActiveTodPackage = useCallback((user: IUser) => {
    return (user?.studentInfo?.minutesLeft ?? 0) > 0
  }, [])

  const getSubscriptionAndBookingStatus = useCallback(
    (user: IUser): SubscriptionAndBookingStatusReturnType => {
      const isSubscriptionRequired = checkIfSubscriptionRequired(user)
      const hasBookings = checkIfUserHasBookings(user)

      return { isSubscriptionRequired, hasBookings }
    },
    [checkIfUserHasBookings, checkIfSubscriptionRequired],
  )

  const runCallbackWithSubscriptionAndBookingStatus = useCallback(
    (user: IUser, onLoginCallback: (isSubscriptionRequired: boolean, hasBookings: boolean) => void): void => {
      const { isSubscriptionRequired, hasBookings } = getSubscriptionAndBookingStatus(user)
      onLoginCallback(isSubscriptionRequired, hasBookings)
    },
    [getSubscriptionAndBookingStatus],
  )

  return {
    checkIfSubscriptionRequired,
    runCallbackWithSubscriptionAndBookingStatus,
    getSubscriptionAndBookingStatus,
    checkIfUserHasActiveTodPackage,
  }
}
